import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Braces, ChevronDown, ChevronRight } from 'lucide-react';
import useAutomationsStore from '../../../../../store/useAutomationsStore';
import { NODE_OUTPUTS, getNodeVariables } from '../../constants/variables';
import { NODE_CONFIG_FIELDS } from '../../constants/fields';

// Build a lookup map: event value → human label for WalletTwo events
const WALLETTWO_EVENT_LABELS = {};
const wtFields = NODE_CONFIG_FIELDS.trigger_wallettwo;
if (wtFields) {
  const eventField = wtFields.find((f) => f.key === 'event');
  if (eventField?.options) {
    for (const opt of eventField.options) {
      WALLETTWO_EVENT_LABELS[opt.value] = opt.label;
    }
  }
}

// Returns a descriptive tag name for a trigger node
function getTriggerTagName(node) {
  const nodeType = node.data?.nodeType;
  if (nodeType === 'trigger_wallettwo') {
    const event = node.data?.event;
    return event ? (WALLETTWO_EVENT_LABELS[event] || event) : 'WalletTwo Event';
  }
  if (nodeType === 'trigger_manual') return 'Manual';
  if (nodeType === 'trigger_schedule') return 'Schedule';
  if (nodeType === 'trigger_webhook') return 'Webhook';
  if (nodeType === 'trigger_onchain') return 'On-Chain';
  return node.data.label;
}

export default function VariablePicker({ onInsert }) {
  const [open, setOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const { nodes, edges, selectedNode } = useAutomationsStore();

  const getAncestors = (nodeId) => {
    const ancestors = new Set();
    const queue = [nodeId];
    while (queue.length > 0) {
      const current = queue.shift();
      for (const edge of edges) {
        if (edge.target === current && !ancestors.has(edge.source)) {
          ancestors.add(edge.source);
          queue.push(edge.source);
        }
      }
    }
    return ancestors;
  };

  const MEMBER_NODE_TYPES = ['wallettwo_get_member', 'wallettwo_get_members'];

  const ancestors = selectedNode ? getAncestors(selectedNode.id) : new Set();
  const hasUpstreamMember = nodes.some(
    (n) => ancestors.has(n.id) && MEMBER_NODE_TYPES.includes(n.data?.nodeType)
  );

  const availableNodes = nodes.filter((n) => {
    if (n.id === selectedNode?.id) return false;
    if (!ancestors.has(n.id)) return false;
    if (!NODE_OUTPUTS[n.data?.nodeType]) return false;
    if (MEMBER_NODE_TYPES.includes(n.data?.nodeType) && !hasUpstreamMember) return false;
    return true;
  });

  // Group nodes by alias, merge variables.
  // For each variable track which node labels provide it.
  // - All nodes in group provide it → no tag
  // - Some nodes provide it → show which ones as tags
  const groups = useMemo(() => {
    const byAlias = {};
    for (const node of availableNodes) {
      const output = getNodeVariables(node);
      if (!output) continue;
      const { alias, variables } = output;
      if (!byAlias[alias]) byAlias[alias] = [];
      byAlias[alias].push({ node, variables });
    }

    return Object.entries(byAlias).map(([alias, entries]) => {
      const totalNodes = entries.length;

      // Collect all variables with the set of descriptive names that provide each
      const varMap = new Map(); // key → { label, providers: string[] }
      for (const { node, variables } of entries) {
        const tagName = getTriggerTagName(node);
        for (const v of variables) {
          if (!varMap.has(v.key)) {
            varMap.set(v.key, { label: v.label, providers: [] });
          }
          varMap.get(v.key).providers.push(tagName);
        }
      }

      const variables = [];
      for (const [key, { label, providers }] of varMap) {
        variables.push({
          key,
          label,
          // Show tags only when not all nodes provide this variable
          tags: providers.length < totalNodes ? providers : null,
        });
      }

      return { alias, nodeCount: totalNodes, variables };
    });
  }, [availableNodes]);

  const btnRef = useRef(null);
  const dropdownRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  const updatePos = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  }, []);

  useEffect(() => {
    if (open) updatePos();
  }, [open, updatePos]);

  // Close on click outside (check both button and dropdown)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (groups.length === 0) return null;

  const toggleGroup = (alias) => {
    setExpandedGroups((prev) => ({ ...prev, [alias]: !prev[alias] }));
  };

  const handleSelect = (alias, variable) => {
    const isTrigger = alias === 'trigger';
    onInsert(isTrigger ? `{{${variable.key}}}` : `{{${alias}.${variable.key}}}`);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-1.5 py-1 text-[11px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition"
        title="Insert variable"
      >
        <Braces className="w-3 h-3" />
        Variable
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-80 bg-white rounded-xl shadow-lg border border-gray-200 max-h-80 overflow-y-auto"
          style={{ top: pos.top, right: pos.right, zIndex: 9999 }}
        >
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              Available Variables
            </p>
          </div>
          <div className="p-1">
            {groups.map((group) => {
              const isExpanded = expandedGroups[group.alias];

              return (
                <div key={group.alias}>
                  <button
                    onClick={() => toggleGroup(group.alias)}
                    className="flex items-center gap-2 w-full px-2 py-1.5 text-left rounded-lg hover:bg-gray-50 transition"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-3 h-3 text-gray-400 shrink-0" />
                    ) : (
                      <ChevronRight className="w-3 h-3 text-gray-400 shrink-0" />
                    )}
                    <code className="text-[11px] font-mono font-semibold text-gray-700">{group.alias}</code>
                    <span className="text-[10px] text-gray-400 ml-auto shrink-0">
                      {group.variables.length}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="ml-5 space-y-0.5 pb-1">
                      {group.variables.map((v) => (
                        <button
                          key={v.key}
                          onClick={() => handleSelect(group.alias, v)}
                          className="flex flex-col items-start w-full px-2 py-1.5 text-left rounded-md hover:bg-blue-50 transition group"
                        >
                          <div className="flex items-center gap-2 w-full">
                            <code className="text-[10px] text-blue-600 bg-blue-50 group-hover:bg-blue-100 px-1.5 py-0.5 rounded font-mono shrink-0">
                              {group.alias === 'trigger' ? v.key : `${group.alias}.${v.key}`}
                            </code>
                            <span className="text-[11px] text-gray-500 truncate">{v.label}</span>
                          </div>
                          {v.tags && (
                            <div className="flex flex-wrap gap-1 mt-1 ml-0.5">
                              {v.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="text-[9px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full whitespace-nowrap"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
